import { useCallback, useMemo, useRef } from 'react';
import { getAnnotationCategory } from '../../utils/annotationCategories';
import { MarkSource, TranscriptMark, transcriptMarksFrom } from '../../utils/transcriptMarks';
import type { TranscriptMarkRequest, TranscriptMarkingProps } from './transcriptMarking';

/**
 * The studio's side of marking the transcript: saving a Remove or Short
 * (and deleting the Removes it absorbs), deleting a mark, and Undo for both.
 * Each studio supplies how a note is saved and deleted: the multicam studio
 * sends each change as its own request, the single-video studio changes its
 * list and autosaves.
 */

export type NewNote = Omit<MarkSource, 'id'>;

export type MarkNotify = (
  message: string,
  severity?: 'success' | 'error' | 'info' | 'warning',
  undo?: () => void
) => void;

export interface TranscriptMarkStore {
  /** Saves a note; resolves with its id, or null when it failed (the studio has said why). */
  add: (note: NewNote) => Promise<string | null>;
  /** Deletes a note; resolves with a function that puts it back, or null when it failed. */
  remove: (id: string) => Promise<(() => Promise<unknown>) | null>;
}

export interface UseTranscriptMarksOptions {
  /** The studio's notes, in timeline seconds (no camera cuts). */
  notes: MarkSource[];
  /** Marks show, but can't be added or deleted (a submitted set). */
  readOnly: boolean;
  /** Why marking can't be used yet (the notes are loading). */
  disabledReason?: string | null;
  selectedId: string | null;
  store: TranscriptMarkStore;
  notify: MarkNotify;
  /** Select a mark and go there. */
  onSelect: (mark: TranscriptMark) => void;
  onClearSelection?: () => void;
  onEdit?: (mark: TranscriptMark) => void;
}

/** What the snackbar says once a mark is saved. */
export const markSavedMessage = (request: Pick<TranscriptMarkRequest, 'kind'>, joined: number): string => {
  if (request.kind === 'short') return 'Marked as a short';
  if (joined === 0) return 'Marked to remove';
  return joined === 1 ? 'Marked to remove, joined with the Remove next to it' : `Marked to remove, joined with ${joined} Removes next to it`;
};

/** What the snackbar says once a mark is deleted. */
export const markDeletedMessage = (mark: Pick<TranscriptMark, 'kind'>): string => {
  if (mark.kind === 'remove') return 'Remove mark deleted';
  if (mark.kind === 'short') return 'Short deleted';
  return 'Note deleted';
};

export function useTranscriptMarks({
  notes,
  readOnly,
  disabledReason = null,
  selectedId,
  store,
  notify,
  onSelect,
  onClearSelection,
  onEdit,
}: UseTranscriptMarksOptions): TranscriptMarkingProps {
  const marks = useMemo(() => transcriptMarksFrom(notes), [notes]);
  // Undo runs later, against whatever the notes are by then.
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const storeRef = useRef(store);
  storeRef.current = store;
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const onMark = useCallback(async (request: TranscriptMarkRequest): Promise<boolean> => {
    const id = await storeRef.current.add({
      time: request.start,
      endTime: request.end,
      text: request.text,
      category: request.kind,
      color: getAnnotationCategory(request.kind).color,
    });
    if (!id) return false;
    // The Removes this one absorbs go once it is saved, so a failure halfway
    // leaves an overlap (harmless: the editor lifts the union) and loses nothing.
    const restores: Array<() => Promise<unknown>> = [];
    for (let i = 0; i < request.replaces.length; i += 1) {
      // One at a time, in order (the multicam studio sends each as its own request anyway).
      const restore = await storeRef.current.remove(request.replaces[i].id);
      if (restore) restores.push(restore);
    }
    notifyRef.current(markSavedMessage(request, restores.length), 'success', () => {
      void (async () => {
        if (notesRef.current.some((note) => note.id === id)) await storeRef.current.remove(id);
        for (let i = 0; i < restores.length; i += 1) {
          await restores[i]();
        }
      })();
    });
    return true;
  }, []);

  const onDeleteMark = useCallback((mark: TranscriptMark) => {
    void (async () => {
      const restore = await storeRef.current.remove(mark.id);
      if (!restore) return;
      notifyRef.current(markDeletedMessage(mark), 'success', () => {
        void restore();
      });
    })();
  }, []);

  return useMemo<TranscriptMarkingProps>(() => ({
    marks,
    selectedMarkId: selectedId,
    readOnly,
    disabledReason: readOnly ? null : disabledReason,
    onSelectMark: onSelect,
    onClearSelection,
    onMark: readOnly ? undefined : onMark,
    onDeleteMark: readOnly ? undefined : onDeleteMark,
    onEditMark: readOnly ? undefined : onEdit,
  }), [marks, selectedId, readOnly, disabledReason, onSelect, onClearSelection, onMark, onDeleteMark, onEdit]);
}
